<script setup lang="ts">
import { computed } from 'vue';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import type { TrackBanEverywherePlan } from '@/composables/api/useLiveTrackUsagesApi';

/**
 * The confirm step before "ban everywhere" writes anything. Every number is the server's plan
 * over the full set of zones (meta.ban_everywhere), not a count of the rows on screen, which the
 * usages endpoint caps.
 */
const props = defineProps<{
  trackName: string | null;
  plan: TrackBanEverywherePlan;
  pending: boolean;
}>();

const open = defineModel<boolean>('open', { required: true });

const emit = defineEmits<{ confirm: [] }>();

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

const title = computed(() =>
  props.trackName ? `Ban “${props.trackName}” everywhere?` : 'Ban this track everywhere?'
);

/** One sentence per fact, and only the facts that apply. */
const banFacts = computed(() => {
  const facts: string[] = [];
  const parts: string[] = [];
  if (props.plan.zones_to_ban > 0) {
    parts.push(`${plural(props.plan.zones_to_ban, 'zone gets', 'zones get')} a new ban.`);
  }
  if (props.plan.zones_already_banned > 0) {
    parts.push(`${plural(props.plan.zones_already_banned, 'zone already bans', 'zones already ban')} it.`);
  }
  if (props.plan.zones_inheriting_ban > 0) {
    parts.push(`${plural(props.plan.zones_inheriting_ban, 'zone inherits', 'zones inherit')} the ban from its master.`);
  }
  if (parts.length > 0) {
    facts.push(parts.join(' '));
  }
  if (props.plan.subscriber_zones_to_push > 0) {
    facts.push(`Players in ${plural(props.plan.subscriber_zones_to_push, 'subscriber zone are', 'subscriber zones are')} told right away too.`);
  }
  if (props.plan.hand_built_lists_losing_track > 0) {
    facts.push(`The track is removed from ${plural(props.plan.hand_built_lists_losing_track, 'hand-built list', 'hand-built lists')}.`);
  }
  facts.push('Includes, requests and static bucket tracks for it are removed in these zones.');
  return facts;
});

const confirmLabel = computed(() =>
  props.plan.zones_to_ban > 0
    ? `Ban in ${plural(props.plan.zones, 'zone', 'zones')}`
    : `Remove from ${plural(props.plan.zones, 'zone', 'zones')}`
);
</script>

<template>
  <Dialog v-model:open="open">
    <DialogContent data-testid="track-ban-everywhere-dialog">
      <DialogHeader>
        <DialogTitle>{{ title }}</DialogTitle>
        <DialogDescription>
          This bans the track in {{ plural(plan.zones, 'zone', 'zones') }} and removes it from what they play.
        </DialogDescription>
      </DialogHeader>
      <ul class="text-foreground/80 list-disc space-y-1 pl-5 text-sm" data-testid="track-ban-everywhere-facts">
        <li v-for="fact in banFacts" :key="fact">{{ fact }}</li>
      </ul>
      <p
        v-if="plan.hand_built_lists_losing_track > 0"
        class="rounded bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-950 dark:text-amber-200"
      >
        Unbanning later will not put the track back on the hand-built lists.
      </p>
      <DialogFooter>
        <Button variant="outline" :disabled="pending" @click="open = false">Cancel</Button>
        <Button
          variant="destructive"
          :disabled="pending"
          data-testid="track-ban-everywhere-confirm"
          @click="emit('confirm')"
        >
          {{ pending ? 'Banning…' : confirmLabel }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
